import { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { logout } from '../store/slices/authSlice';
import { useSessionTimeout } from '../hooks/useSessionTimeout';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  VpnKey as LicenseIcon,
  People as UserIcon,
  Apps as AppIcon,
  Person as PersonIcon,
  Devices as DeviceIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';

const drawerWidth = 240;

// ロールベースメニュー項目の定義
const getMenuItems = (userRole: string | undefined) => {
  const adminItems = [
    { text: 'ダッシュボード', icon: <DashboardIcon />, path: '/', testId: 'nav-dashboard' },
    { text: 'ライセンス管理', icon: <LicenseIcon />, path: '/licenses', testId: 'nav-licenses' },
    { text: 'ユーザー管理', icon: <UserIcon />, path: '/users', testId: 'nav-users' },
    { text: 'マイアプリケーション', icon: <AppIcon />, path: '/applications', testId: 'nav-applications' },
    { text: 'オーナー設定', icon: <SettingsIcon />, path: '/owner-settings', testId: 'nav-owner-settings' },
    { text: 'デバイス管理', icon: <DeviceIcon />, path: '/devices', testId: 'nav-devices' },
  ];

  const userItems = [
    { text: 'ダッシュボード', icon: <DashboardIcon />, path: '/', testId: 'nav-dashboard' },
    { text: 'マイページ', icon: <PersonIcon />, path: '/mypage', testId: 'nav-mypage' },
    { text: 'マイライセンス', icon: <LicenseIcon />, path: '/my-licenses', testId: 'nav-my-licenses' },
    { text: 'マイデバイス', icon: <DeviceIcon />, path: '/my-devices', testId: 'nav-my-devices' },
  ];

  // 管理者の場合は管理機能のみ表示（マイページなし）
  if (userRole?.toUpperCase() === 'ADMIN') {
    return adminItems;
  }

  // 一般ユーザーの場合はマイページを含む個人向けメニューを表示
  return userItems;
};

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  // 認証状態とユーザー情報を取得
  const { user } = useSelector((state: RootState) => state.auth);

  // ユーザーロールに基づいてメニュー項目を取得
  const menuItems = getMenuItems(user?.role);

  // セッションタイムアウトフックを使用
  useSessionTimeout();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    // 即座にストレージをクリア（競合を防ぐため）
    localStorage.removeItem('accessToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    console.log('🔒 Layout: ローカルストレージを即座にクリアしました');

    // 即座にログインページへ遷移
    navigate('/login', { replace: true });

    // バックグラウンドでログアウト処理を実行
    try {
      await dispatch(logout());
      console.log('✅ Layout: ログアウト処理完了');
    } catch (error) {
      console.warn('⚠️ Layout: ログアウト処理でエラー（遷移は完了済み）:', error);
    }
  };

  const drawer = (variant: 'temporary' | 'permanent') => (
    <div>
      <Toolbar />
      <List data-testid={`nav-menu-${variant}`}>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            onClick={() => navigate(item.path)}
            data-testid={`${item.testId}-${variant}`}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            ライセンス認証サーバー
          </Typography>
          <Button color="inherit" onClick={handleLogout} data-testid="logout-button">
            ログアウト
          </Button>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer('temporary')}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer('permanent')}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
} 